import React from "react";
import "../styles/PrintPage.css";
import { Box, Button, IconPrint, Text, Alert, Stack } from "braid-design-system";
import { useLocation } from "react-router-dom";
import { getPlAccount, getPlAccountPrintStatus, markPlAccountPrinted, PlAccountResponse } from "../services/plAccountService";
import { CustomerResponse } from "../services/approveMobileCustomerService";
import apiClient from "../clients/apiClient";
import Loading from "../components/Loading";
import { formatCurrency } from "../utils/currencyUtils";
import { formatPrintDate } from "../utils/dateUtils";
import { useToast } from "../hooks/useToast";
import { getPrintConfig } from "../services/transactionService";
import policyService from "../services/policyService";
import { numberToWords } from "../utils/numberToWords";
import { getFieldLabel } from "../utils/printLabels";
import { openPrintWindowHTML } from "../utils/printUtils";

interface FdCertificatePrintProps {
    accountId?: number;
}

// pl_policy_type id for "Period Policy" (Day / Week / Month rows live under it)
const PERIOD_POLICY_TYPE_ID = 10;

// singular english base word; si/ta already use the display form
const PERIOD_UNIT_FALLBACK = { en: "Month", si: "මාස", ta: "மாதங்கள்" };

const PERIOD_UNITS: Record<number, { en: string; si: string; ta: string }> = {
    100: { en: "Day", si: "දින", ta: "நாட்கள்" },
    101: { en: "Week", si: "සති", ta: "வாரங்கள்" },
    102: { en: "Month", si: "මාස", ta: "மாதங்கள்" },
};

const FdCertificatePrint: React.FC<FdCertificatePrintProps> = ({ accountId: propAccountId }) => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const accountId = propAccountId || Number(queryParams.get('accountId'));

    const [loading, setLoading] = React.useState(true);
    const [printing, setPrinting] = React.useState(false);
    const [account, setAccount] = React.useState<PlAccountResponse | null>(null);
    const [customer, setCustomer] = React.useState<CustomerResponse | null>(null);
    const [printConfig, setPrintConfig] = React.useState<any>(null);
    const [printConfigError, setPrintConfigError] = React.useState<string>("");
    const [isPrinted, setIsPrinted] = React.useState(false);
    const [subCategories, setSubCategories] = React.useState<string[]>([]);
    const [selectedSubCategory, setSelectedSubCategory] = React.useState<string>("");
    const [fullLoadedConfig, setFullLoadedConfig] = React.useState<any>(null);
    const [periodPolicies, setPeriodPolicies] = React.useState<any[]>([]);

    const toast = useToast();
    const [printLang, setPrintLang] = React.useState('si');

    // Dynamic / custom info fields loaded from backend before printing
    const [customerInfos, setCustomerInfos] = React.useState<any[]>([]);
    const [accountInfos, setAccountInfos] = React.useState<any[]>([]);

    const getCustomerById = async (customerId: number): Promise<CustomerResponse | null> => {
        try {
            const response = await apiClient.get(`/customer/id/${customerId}`);
            return response.data.data || null;
        } catch {
            return null;
        }
    };

    React.useEffect(() => {
        const fetchData = async () => {
            if (!accountId) return;
            setLoading(true);
            try {
                const [accountData, config, printStatusRes, periodPoliciesData] = await Promise.all([
                    getPlAccount(accountId),
                    getPrintConfig(accountId, 'FD_CERTIFICATE'),
                    getPlAccountPrintStatus(accountId).catch(() => null),
                    policyService.getPoliciesByType(PERIOD_POLICY_TYPE_ID).catch(() => [] as any[]),
                ]);

                setAccount(accountData);
                setPeriodPolicies(periodPoliciesData || []);

                if (printStatusRes?.printed === 1) {
                    setIsPrinted(true);
                }

                if (accountData?.ci_customer_id) {
                    const customerData = await getCustomerById(accountData.ci_customer_id);
                    setCustomer(customerData);
                }

                // Fetch customer custom fields and account custom fields
                let customerInfoData: any[] = [];
                let accountInfoData: any[] = [];

                if (accountData?.ci_customer_id) {
                    try {
                        const infoRes = await apiClient.get(`/customer/${accountData.ci_customer_id}/info`);
                        customerInfoData = infoRes.data?.data || [];
                    } catch (err) {
                        // eslint-disable-next-line no-console
                        console.error("Error fetching customer info fields:", err);
                    }
                }

                if (accountId) {
                    try {
                        const infoRes = await apiClient.get(`/loan-application/account_info/${accountId}`);
                        accountInfoData = infoRes.data?.data || [];
                    } catch (err) {
                        // eslint-disable-next-line no-console
                        console.error("Error fetching account info fields:", err);
                    }
                }

                setCustomerInfos(customerInfoData);
                setAccountInfos(accountInfoData);

                if (config && typeof config === 'object' && Object.keys(config).length > 0) {
                    setFullLoadedConfig(config);
                    let resolvedCfg = config;
                    if (Array.isArray(config.sub_sections)) {
                        const subKeys = config.sub_sections.map((obj: any) => Object.keys(obj)[0]).filter(Boolean);
                        setSubCategories(subKeys);
                        setSelectedSubCategory("");
                        // Strip sub_sections to get default config
                        const defaultCfg = { ...config };
                        delete defaultCfg.sub_sections;
                        resolvedCfg = defaultCfg;
                    } else {
                        setSubCategories([]);
                        setSelectedSubCategory("");
                    }
                    setPrintConfig(resolvedCfg);
                    setPrintLang(resolvedCfg.default_lang || "en");
                    setPrintConfigError("");
                } else {
                    setPrintConfig({});
                    setPrintConfigError("No FD Certificate print configuration found. Please set it up in Print Policy Config.");
                }
            } catch (error: any) {
                toast.showError(`Error loading FD Certificate data: ${error.message}`);
                setPrintConfig({});
                setPrintConfigError(`Failed to load print config: ${error.message}`);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [accountId]);

    // Handle sub-category switch
    const handleSubCategoryChange = (name: string) => {
        setSelectedSubCategory(name);
        if (!fullLoadedConfig) return;
        if (name) {
            const foundObj = (fullLoadedConfig.sub_sections || []).find((obj: any) => Object.keys(obj)[0] === name);
            setPrintConfig(foundObj ? foundObj[name] : {});
            const defaultLanguage = (foundObj ? foundObj[name] : {})?.default_lang || fullLoadedConfig.default_lang || "en";
            setPrintLang(defaultLanguage);
        } else {
            const defaultCfg = { ...fullLoadedConfig };
            delete defaultCfg.sub_sections;
            setPrintConfig(defaultCfg);
            setPrintLang(defaultCfg.default_lang || "en");
        }
    };

    const formatDate = (date: string | Date | null | undefined): string => {
        if (!date) return '';
        if (printConfig?.date_format) {
            return formatPrintDate(date, printConfig.date_format);
        }
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const getLocalizedValue = (obj: any, baseKey: string, lang?: string, fieldId?: string) => {
        if (!obj) return '';
        const currentLang = lang || printLang || localStorage.getItem('app_lang') || 'en';

        let isTranslate = true;
        if (fieldId && printConfig && printConfig[fieldId] && printConfig[fieldId].translate !== undefined) {
            isTranslate = Boolean(printConfig[fieldId].translate);
        } else if (fieldId) {
            const cleanKey = fieldId.replace(/_copy_\d+$/, "");
            if (printConfig && printConfig[cleanKey] && printConfig[cleanKey].translate !== undefined) {
                isTranslate = Boolean(printConfig[cleanKey].translate);
            }
        }

        const cleanLang = isTranslate ? (currentLang.startsWith("si") ? "si" : currentLang.startsWith("ta") ? "ta" : "en") : "en";
        if (cleanLang === "si" && obj[`${baseKey}_ln2`]?.trim()) return obj[`${baseKey}_ln2`].trim();
        if (cleanLang === "ta" && obj[`${baseKey}_ln3`]?.trim()) return obj[`${baseKey}_ln3`].trim();
        const fallback = obj[`${baseKey}_ln1`] || obj[baseKey];
        return typeof fallback === 'string' ? fallback.trim() : String(fallback ?? '');
    };

    const getPeriodUnit = (lang: "si" | "ta" | "en", value?: number | string | null): string => {
        const num = value !== undefined && value !== null && value !== "" ? Number(value) : NaN;
        const policyId = (account as any)?.account_type?.period_policy ?? null;
        let base = PERIOD_UNIT_FALLBACK[lang];
        const known = policyId != null ? PERIOD_UNITS[policyId] : undefined;
        if (known) {
            base = known[lang];
        } else if (policyId != null) {
            const policy = periodPolicies.find((p: any) => p.id === policyId);
            base = policy
                ? (lang === "si" ? policy.policy_name_ln2 : lang === "ta" ? policy.policy_name_ln3 : policy.policy_name_ln1)
                    || policy.policy_name_ln1
                    || base
                : base;
        }
        if (lang !== "en") return base;
        const isSingle = Number.isFinite(num) && num === 1;
        return isSingle || /s$/i.test(base) ? base : `${base}s`;
    };

    const getFieldValue = (field: string): string => {
        if (!account && !customer) return '';
        const currentLang = printLang || localStorage.getItem('app_lang') || 'en';
        const baseField = field.replace(/_copy_\d+$/, "");

        switch (baseField) {
            case 'institute_name':
                return getLocalizedValue((account?.branch as any)?.institute, 'name', currentLang, field) || getLocalizedValue(account, 'institute_name', currentLang, field) || '';
            case 'open_date':
            case 'deposit_date':
                return formatDate(account?.open_date);
            case 'closing_date':
            case 'maturity_date':
            case 'reneve_date':
            case 'renew_date':
                return formatDate((account as any)?.reneve_date);
            case 'open_time': {
                if (account?.open_date) {
                    const d = new Date(account.open_date);
                    if (!isNaN(d.getTime())) {
                        return d.toTimeString().split(' ')[0];
                    }
                }
                return '';
            }
            case 'interest_rate_by_12':
            case 'interest_rate/12': {
                const rate = account?.interest_rate != null ? Number(account.interest_rate) : NaN;
                return isNaN(rate) ? '' : (rate / 12).toFixed(2);
            }
            case 'ref_account_number':
            case 'account_number':
                return account?.ref_account_number || account?.account_number || '';
            case 'period': {
                const isHideMonths = printConfig?.[field]?.hide_months === true || printConfig?.[field]?.hide_months === 1 || printConfig?.[field]?.hide_months === "true" ||
                    printConfig?.[baseField]?.hide_months === true || printConfig?.[baseField]?.hide_months === 1 || printConfig?.[baseField]?.hide_months === "true";
                if (isHideMonths) return account?.period ? String(account.period) : '';
                const isFieldTranslate = printConfig?.[field]?.translate !== undefined
                    ? Boolean(printConfig[field].translate)
                    : printConfig?.period?.translate !== undefined
                        ? Boolean(printConfig.period.translate)
                        : true;
                const cleanLang = isFieldTranslate ? (currentLang.startsWith("si") ? "si" : currentLang.startsWith("ta") ? "ta" : "en") : "en";
                const periodUnit = getPeriodUnit(cleanLang, account?.period);
                return cleanLang === 'si' ? `${periodUnit} ${account?.period || ''}` : cleanLang === 'ta' ? `${periodUnit} ${account?.period || ''}` : `${account?.period || ''} ${periodUnit}`;
            }
            case 'period_minus_one': {
                const periodVal = account?.period ? parseInt(String(account.period), 10) : 0;
                const calculatedPeriod = periodVal > 0 ? periodVal - 1 : '';
                if (!calculatedPeriod) return '';
                const isHideMonths = printConfig?.[field]?.hide_months === true || printConfig?.[field]?.hide_months === 1 || printConfig?.[field]?.hide_months === "true" ||
                    printConfig?.[baseField]?.hide_months === true || printConfig?.[baseField]?.hide_months === 1 || printConfig?.[baseField]?.hide_months === "true";
                if (isHideMonths) return String(calculatedPeriod);
                const isFieldTranslate = printConfig?.[field]?.translate !== undefined
                    ? Boolean(printConfig[field].translate)
                    : printConfig?.period?.translate !== undefined
                        ? Boolean(printConfig.period.translate)
                        : true;
                const cleanLang = isFieldTranslate ? (currentLang.startsWith("si") ? "si" : currentLang.startsWith("ta") ? "ta" : "en") : "en";
                const periodUnit = getPeriodUnit(cleanLang, calculatedPeriod);
                return cleanLang === 'si' ? `${periodUnit} ${calculatedPeriod}` : cleanLang === 'ta' ? `${periodUnit} ${calculatedPeriod}` : `${calculatedPeriod} ${periodUnit}`;
            }
            case 'balance':
                return (account?.balance !== undefined && account?.balance !== null) ? `${formatCurrency(account.balance)}` : '';
            case 'balance_words': {
                const isFieldTranslate = printConfig?.[field]?.translate !== undefined
                    ? Boolean(printConfig[field].translate)
                    : printConfig?.balance_words?.translate !== undefined
                        ? Boolean(printConfig.balance_words.translate)
                        : true;
                return (account?.balance !== undefined && account?.balance !== null) ? numberToWords(account.balance, isFieldTranslate ? currentLang : "en") : '';
            }
            case 'interest_rate': {
                const isHidePercent = printConfig?.[field]?.hide_percent === true || printConfig?.[field]?.hide_percent === 1 || printConfig?.[field]?.hide_percent === "true" ||
                    printConfig?.[baseField]?.hide_percent === true || printConfig?.[baseField]?.hide_percent === 1 || printConfig?.[baseField]?.hide_percent === "true";
                return account?.interest_rate ? (isHidePercent ? `${account.interest_rate}` : `${account.interest_rate}%`) : '';
            }
            case 'interest_rate_period': {
                const isHidePercent = printConfig?.[field]?.hide_percent === true || printConfig?.[field]?.hide_percent === 1 || printConfig?.[field]?.hide_percent === "true" ||
                    printConfig?.[baseField]?.hide_percent === true || printConfig?.[baseField]?.hide_percent === 1 || printConfig?.[baseField]?.hide_percent === "true";
                const isFieldTranslate = printConfig?.[field]?.translate !== undefined
                    ? Boolean(printConfig[field].translate)
                    : printConfig?.period?.translate !== undefined
                        ? Boolean(printConfig.period.translate)
                        : true;
                const cleanLang = isFieldTranslate ? (currentLang.startsWith("si") ? "si" : currentLang.startsWith("ta") ? "ta" : "en") : "en";
                const periodStr = (() => {
                    const periodUnit = getPeriodUnit(cleanLang, account?.period);
                    return cleanLang === 'si' ? `${periodUnit} ${account?.period || ''}` : cleanLang === 'ta' ? `${periodUnit} ${account?.period || ''}` : `${account?.period || ''} ${periodUnit}`;
                })();
                const rateStr = account?.interest_rate ? (isHidePercent ? `${account.interest_rate}` : `${account.interest_rate}%`) : '';
                return `Per ${periodStr} rate ${rateStr}`;
            }
            case 'customer_nic':
            case 'nic':
                return customer?.nic || '';
            case 'address':
                return getLocalizedValue(customer, 'address', currentLang, field);
            case 'full_name':
                return getLocalizedValue(customer, 'full_name', currentLang, field);
            case 'address_ln1':
            case 'address_ln2':
            case 'address_ln3': {
                const isFieldTranslate = printConfig?.[field]?.translate !== undefined
                    ? Boolean(printConfig[field].translate)
                    : printConfig?.address?.translate !== undefined
                        ? Boolean(printConfig.address.translate)
                        : true;
                const cleanLang = isFieldTranslate ? (currentLang.startsWith("si") ? "si" : currentLang.startsWith("ta") ? "ta" : "en") : "en";
                if (cleanLang === "si") return (customer as any)?.address_ln2 || customer?.address_ln1 || '';
                if (cleanLang === "ta") return (customer as any)?.address_ln3 || customer?.address_ln1 || '';
                return customer?.address_ln1 || '';
            }
            case 'full_name_ln1':
            case 'full_name_ln2':
            case 'full_name_ln3': {
                const isFieldTranslate = printConfig?.[field]?.translate !== undefined
                    ? Boolean(printConfig[field].translate)
                    : printConfig?.full_name?.translate !== undefined
                        ? Boolean(printConfig.full_name.translate)
                        : true;
                const cleanLang = isFieldTranslate ? (currentLang.startsWith("si") ? "si" : currentLang.startsWith("ta") ? "ta" : "en") : "en";
                if (cleanLang === "si") return (customer as any)?.full_name_ln2 || (customer as any)?.full_name_ln1 || '';
                if (cleanLang === "ta") return (customer as any)?.full_name_ln3 || (customer as any)?.full_name_ln1 || '';
                return (customer as any)?.full_name_ln1 || '';
            }
            case 'mobile_1':
                return customer?.mobile_1 || '';
            case 'c_by':
                return (account as any)?.c_by != null ? String((account as any).c_by) : '';
            case 'c_by_name':
                return (account as any)?.c_by_name || '';
            case 'voucher_number':
                return (account as any)?.voucher_number || '';
            case 'print_time':
                return formatPrintDate(new Date(), printConfig?.time_format || 'HH:mm:ss');
            case 'print_date':
                return formatPrintDate(new Date(), printConfig?.date_format);
            case 'reprint':
                return isPrinted ? `RE-PRINT ${formatDate(new Date())} Admin` : '';
            default: {
                if (baseField === 'institute_address') {
                    return getLocalizedValue((account?.branch as any)?.institute, 'address', currentLang, field) || (account as any)?.institute_address || '';
                }
                if (baseField === 'institute_email') {
                    return (account?.branch as any)?.institute?.email || (account as any)?.institute_email || '';
                }
                if (baseField === 'institute_reg_num') {
                    return (account?.branch as any)?.institute?.registration_number || (account?.branch as any)?.institute?.reg_num || (account as any)?.institute_reg_num || '';
                }
                if (baseField === 'institute_reg_date') {
                    const rawDate = (account?.branch as any)?.institute?.registration_date || (account?.branch as any)?.institute?.reg_date || (account as any)?.institute_reg_date || '';
                    return formatPrintDate(rawDate, printConfig?.date_format);
                }
                if (baseField === 'branch_name') {
                    return getLocalizedValue(account?.branch, 'name', currentLang, field);
                }
                if (baseField === 'branch_address') {
                    return getLocalizedValue(account?.branch, 'address', currentLang, field);
                }
                if (baseField === 'branch_email') {
                    return account?.branch?.e_mail || (account as any)?.branch_email || '';
                }
                if (baseField === 'institute_phone_number' || baseField === 'institute_phone') {
                    return (account?.branch as any)?.institute?.phone || '';
                }
                if (baseField === 'branch_telephone_number' || baseField === 'branch_telephone') {
                    return account?.branch?.telephone || '';
                }
                if (baseField === 'member_number') {
                    return customer?.customer_number || '';
                }
                if (baseField === 'mobile_number') {
                    return customer?.mobile_1 || customer?.mobile_2 || '';
                }

                // Check customer custom fields
                if (customerInfos.length > 0) {
                    const matchCust = customerInfos.find(info => {
                        const infoName = (info.field_name || '').toLowerCase().replace(/\s+/g, '_');
                        const targetName = baseField.toLowerCase().replace(/\s+/g, '_');
                        return infoName === targetName || info.field_name === baseField;
                    });
                    if (matchCust) return matchCust.value || '';
                }

                // Check account custom fields
                if (accountInfos.length > 0) {
                    const matchAcc = accountInfos.find(info => {
                        const infoName = (info.pl_account_type_info?.field_name || '').toLowerCase().replace(/\s+/g, '_');
                        const targetName = baseField.toLowerCase().replace(/\s+/g, '_');
                        return infoName === targetName || info.pl_account_type_info?.field_name === baseField;
                    });
                    if (matchAcc) return matchAcc.information || '';
                }

                if (account && baseField in account) {
                    const val = (account as any)[baseField];
                    if (val instanceof Date || (typeof val === 'string' && !isNaN(Date.parse(val)) && (val.includes('-') || val.includes('/')))) {
                        return formatDate(val);
                    }
                    if (typeof val === 'number') {
                        if (baseField.endsWith('rate') || baseField.includes('percent')) {
                            return `${val}%`;
                        }
                        const moneyFields = ['balance', 'capital', 'interest', 'approved', 'minimum_balance', 'lowest_balance', 'penalty', 'fund', 'insurance', 'fee', 'past_due_amount', 'standing_order_amount', 'tax', 'opening_balance', 'capital_installment', 'total_installment', 'save_interest_temp', 'due_interest', 'interest_installment', 'interest_balance', 'total_interest'];
                        if (moneyFields.includes(baseField)) {
                            return formatCurrency(val);
                        }
                        return String(val);
                    }
                    return String(val ?? '');
                }
                if (customer && baseField in customer) {
                    const val = (customer as any)[baseField];
                    if (val instanceof Date || (typeof val === 'string' && !isNaN(Date.parse(val)) && (val.includes('-') || val.includes('/')))) {
                        return formatDate(val);
                    }
                    return String(val ?? '');
                }
                return '';
            }
        }
    };

    const handlePrint = async () => {
        try {
            const printElement = document.getElementById('fd-certificate-offscreen');
            if (!printElement) {
                toast.showError('Print element not found');
                return;
            }
            setPrinting(true);

            // Check latest print status from backend before printing
            let currentlyPrinted = isPrinted;
            try {
                const statusRes = await getPlAccountPrintStatus(accountId);
                currentlyPrinted = statusRes?.printed === 1;
                setIsPrinted(currentlyPrinted);
            } catch {
                // Use local state if the check fails
            }

            const pageWidth = printConfig?.page_width || '21cm';
            const pageHeight = printConfig?.page_height || '29.7cm';

            openPrintWindowHTML(printElement.outerHTML, pageWidth, pageHeight, 'FD Certificate', () => {
                // If this is the first print, mark as printed in the DB
                if (!currentlyPrinted && accountId) {
                    markPlAccountPrinted(accountId)
                        .then(() => { setIsPrinted(true); })
                        .catch(() => {
                            toast.showError('Warning: Could not save print status to server.');
                        });
                }
            });
            setPrinting(false);
        } catch {
            toast.showError('Failed to generate print');
            setPrinting(false);
        }
    };

    if (loading) {
        return <Loading message="Loading FD Certificate data..." />;
    }

    const hasConfig = printConfig && printConfig.required_fields && printConfig.required_fields.length > 0;

    return (
        <Box style={{ position: 'relative', width: '100%', overflow: 'auto' }}>
            {printConfigError && (
                <Box paddingBottom="medium">
                    <Alert tone="caution">
                        <Text size="small">{printConfigError}</Text>
                    </Alert>
                </Box>
            )}

            {hasConfig && (
                <>
                    {/* Calibration Toolbar */}
                    <Box className="no-print" paddingBottom="medium" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', width: printConfig?.page_width || '21cm', margin: '0 auto' }}>
                        <Text tone="secondary" size="small" weight="strong">
                            📐 Actual Page Size: {printConfig?.page_width || '21cm'} (Width) x {printConfig?.page_height || '29.7cm'} (Height)
                        </Text>
                        <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <select
                                value={printLang}
                                onChange={(e) => setPrintLang(e.target.value)}
                                style={{
                                    padding: '4px 8px',
                                    fontSize: '13px',
                                    borderRadius: '4px',
                                    border: '1px solid #c8ccd4',
                                    backgroundColor: '#fff',
                                    color: '#2b303a',
                                    outline: 'none',
                                    cursor: 'pointer',
                                    height: '32px',
                                    fontWeight: 500,
                                }}
                            >
                                <option value="en">English (EN)</option>
                                <option value="si">Sinhala (SI)</option>
                                <option value="ta">Tamil (TA)</option>
                            </select>
                            {subCategories.length > 0 && (
                                <select
                                    value={selectedSubCategory}
                                    onChange={e => handleSubCategoryChange(e.currentTarget.value)}
                                    style={{
                                        padding: '4px 8px',
                                        fontSize: '13px',
                                        borderRadius: '4px',
                                        border: '1px solid #c8ccd4',
                                        backgroundColor: '#fff',
                                        color: '#2b303a',
                                        outline: 'none',
                                        cursor: 'pointer',
                                        height: '32px',
                                        fontWeight: 500,
                                    }}
                                >
                                    <option value="">Default</option>
                                    {subCategories.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                            )}
                            <Button
                                size="small"
                                tone={isPrinted ? 'critical' : 'brandAccent'}
                                icon={<IconPrint />}
                                onClick={handlePrint}
                                loading={printing}
                            >
                                {isPrinted ? 'Reprint FD Certificate' : 'Print FD Certificate'}
                            </Button>
                        </Box>
                    </Box>
                    <Box style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        {/* The Print Preview Canvas */}
                        <Box
                            style={{
                                border: '1px solid #adb5bd',
                                borderRadius: '8px',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
                                position: 'relative',
                                width: printConfig?.page_width || '21cm',
                                height: printConfig?.page_height || '29.7cm',
                                minWidth: printConfig?.page_width || '21cm',
                                minHeight: printConfig?.page_height || '29.7cm',
                                flexShrink: 0,
                                backgroundColor: '#fff',
                                fontFamily: "'Noto Serif', 'Noto Serif Sinhala', 'Noto Sans Tamil', 'Times New Roman', 'Iskoola Pota', 'Latha', serif",
                                fontSize: printConfig?.font_size || '12px',
                                overflow: 'hidden',
                                backgroundImage: printConfig?.background_image ? `url(${printConfig.background_image})` : undefined,
                                backgroundSize: '100% 100%',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'center',
                                boxSizing: 'border-box',
                                paddingTop: printConfig?.margin_top || '1.0cm',
                                paddingBottom: printConfig?.margin_bottom || '1.0cm',
                                paddingLeft: printConfig?.margin_left || '1.0cm',
                                paddingRight: printConfig?.margin_right || '1.0cm',
                            }}
                        >
                            <Box style={{ position: 'absolute', top: '10px', left: '15px', opacity: 0.6, zIndex: 5 }} className="no-print">
                                <Text size="xsmall" tone="secondary" weight="strong">
                                    FD CERTIFICATE PREVIEW
                                </Text>
                            </Box>
                            {printConfig.required_fields.map((field: string) => {
                                const value = getFieldValue(field);
                                const showLabel = Boolean(printConfig[`${field}_show_label`]);
                                const currentLang = printLang || localStorage.getItem('app_lang') || 'en';
                                return (
                                    <React.Fragment key={`preview-${field}`}>
                                        {showLabel && (
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    left: printConfig[`${field}_label_left`] || '0cm',
                                                    top: printConfig[`${field}_label_top`] || '0cm',
                                                    fontWeight: printConfig[field]?.bold ? 'bold' : 'normal',
                                                    fontStyle: printConfig[field]?.italic ? 'italic' : 'normal',
                                                    fontSize: printConfig[field]?.font_size || 'inherit',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {getFieldLabel(field, currentLang, printConfig)}
                                            </div>
                                        )}
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: printConfig[`${field}_left`] || '0cm',
                                                top: printConfig[`${field}_top`] || '0cm',
                                                fontWeight: printConfig[field]?.bold ? 'bold' : 'normal',
                                                fontStyle: printConfig[field]?.italic ? 'italic' : 'normal',
                                                fontSize: printConfig[field]?.font_size || 'inherit',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {value || `[${field}]`}
                                        </div>
                                    </React.Fragment>
                                );
                            })}
                        </Box>
                    </Box>


                </>
            )}

            {!hasConfig && !printConfigError && (
                <Alert tone="info">
                    <Stack space="small">
                        <Text weight="strong">No Configuration</Text>
                        <Text size="small">
                            Please configure FD Certificate layout in Print Policy Config before printing.
                        </Text>
                    </Stack>
                </Alert>
            )}

            {/* Off-screen Print Container */}
            {hasConfig && (
                <div style={{ position: 'absolute', top: '-10000px', left: '-10000px' }}>
                    <div
                        id="fd-certificate-offscreen"
                        style={{
                            width: printConfig?.page_width || '21cm',
                            height: printConfig?.page_height || '29.7cm',
                            paddingTop: printConfig?.margin_top || '1.0cm',
                            paddingBottom: printConfig?.margin_bottom || '1.0cm',
                            paddingLeft: printConfig?.margin_left || '1.0cm',
                            paddingRight: printConfig?.margin_right || '1.0cm',
                            fontSize: printConfig?.font_size || '12px',
                            fontFamily: "'Noto Serif', 'Noto Serif Sinhala', 'Noto Sans Tamil', 'Times New Roman', 'Iskoola Pota', 'Latha', serif",
                            backgroundColor: '#ffffff',
                            position: 'relative',
                            boxSizing: 'border-box',
                            /* Do not include background image in the offscreen print container so background image does not print */
                            backgroundImage: undefined,
                            backgroundSize: '100% 100%',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                            overflow: 'hidden'
                        }}
                    >
                        {printConfig.required_fields.map((field: string) => {
                            const showLabel = Boolean(printConfig[`${field}_show_label`]);
                            const currentLang = printLang || localStorage.getItem('app_lang') || 'en';
                            return (
                                <React.Fragment key={`print-${field}`}>
                                    {showLabel && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                left: printConfig[`${field}_label_left`] || '0cm',
                                                top: printConfig[`${field}_label_top`] || '0cm',
                                                fontWeight: printConfig[field]?.bold ? 'bold' : 'normal',
                                                fontStyle: printConfig[field]?.italic ? 'italic' : 'normal',
                                                fontSize: printConfig[field]?.font_size || 'inherit',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {getFieldLabel(field, currentLang, printConfig)}
                                        </div>
                                    )}
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: printConfig[`${field}_left`] || '0cm',
                                            top: printConfig[`${field}_top`] || '0cm',
                                            fontWeight: printConfig[field]?.bold ? 'bold' : 'normal',
                                            fontStyle: printConfig[field]?.italic ? 'italic' : 'normal',
                                            fontSize: printConfig[field]?.font_size || 'inherit',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {getFieldValue(field)}
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            )}
        </Box>
    );
};

export default FdCertificatePrint;
